import React from 'react';
import { Code2, Rocket, Shield, Globe, Cpu, Zap, Mail, ArrowLeft, Linkedin, Github } from 'lucide-react';
import { Link } from 'react-router-dom';

export const AboutPage: React.FC = () => {
    const skills = [
        { name: 'Test Automation', level: 'Expert', icon: <Cpu className="w-5 h-5" />, details: 'Selenium, Playwright, Appium' },
        { name: 'DevSecOps', level: 'Advanced', icon: <Shield className="w-5 h-5" />, details: 'CI/CD, Azure Automation, Security Testing' },
        { name: 'Frontend', level: 'Proficient', icon: <Code2 className="w-5 h-5" />, details: 'TypeScript, JavaScript, React' },
        { name: 'Backend', level: 'Proficient', icon: <Zap className="w-5 h-5" />, details: 'API Testing (Postman, Rest-Assured), Node.js' },
        { name: 'Performance', level: 'Advanced', icon: <Rocket className="w-5 h-5" />, details: 'JMeter, K6, Flood.io' },
        { name: 'Accessibility', level: 'Specialist', icon: <Globe className="w-5 h-5" />, details: 'WCAG 2.1/2.2, Section 508, ARIA' },
    ];


    return (
        <div className="max-w-6xl mx-auto px-4 py-12 sm:px-6 lg:px-8 space-y-20">
            {/* Navigation Header */}
            <div className="flex items-center justify-between">
                <Link to="/" className="text-primary-600 hover:text-primary-700 flex items-center font-medium transition-colors">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Scanner
                </Link>
            </div>

            {/* Hero Section */}
            <div className="relative glass rounded-3xl p-8 md:p-12 overflow-hidden">
                <div className="relative z-10 flex flex-col md:flex-row items-center gap-12">
                    <div className="w-48 h-48 md:w-64 md:h-64 flex-shrink-0 relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-primary-400 to-purple-500 rounded-3xl rotate-6 opacity-20"></div>
                        <img
                            src="/profile.jpg"
                            alt="Mohammad Shahbaz"
                            className="w-full h-full object-cover rounded-3xl shadow-2xl relative z-10 border border-white/50"
                        />
                    </div>
                    <div className="flex-1 space-y-6">
                        <div className="inline-flex items-center px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-bold uppercase tracking-wider">
                            Founder & Creator
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 leading-tight">
                            Hey 👋 I'm <span className="text-gradient-primary">Shahbaz</span>
                        </h1>
                        <p className="text-xl text-gray-600 font-medium max-w-2xl leading-relaxed">
                            Software Tester with 8.5+ years of experience in manual and automation testing, dedicated to building inclusive digital experiences.
                        </p>
                        <div className="flex items-center gap-4 pt-4">
                            <a href="https://www.linkedin.com/in/mohammadshahbaz1/" target="_blank" rel="noopener noreferrer" className="p-3 bg-white hover:bg-primary-50 text-gray-600 hover:text-primary-600 rounded-2xl shadow-sm border border-gray-100 transition-all group">
                                <Linkedin className="w-6 h-6 group-hover:scale-110 transition-transform" />
                            </a>
                            <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="p-3 bg-white hover:bg-gray-50 text-gray-600 hover:text-gray-900 rounded-2xl shadow-sm border border-gray-100 transition-all group">
                                <Github className="w-6 h-6 group-hover:scale-110 transition-transform" />
                            </a>
                            <Link to="/contact" className="btn-primary py-3 px-6 rounded-2xl flex items-center gap-2">
                                <Mail className="w-5 h-5" />
                                Get in Touch
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* About Section */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                <div className="lg:col-span-12 space-y-8">
                    <h2 className="text-3xl font-black text-gray-900">My Story</h2>
                    <div className="prose prose-lg text-gray-600 max-w-none space-y-6">
                        <p>
                            I’m Mohammad Shahbaz, a software tester from India with 8.5+ years of experience working on web and desktop applications across both manual and automation testing.
                        </p>
                        <p>
                            I’ve been part of multiple product journeys, working in Agile (Scrum) and V-Model environments, and collaborating closely with product owners, business analysts, and developers. A big part of my work is breaking things before users do — designing solid test cases, creating meaningful test data, and covering everything from functional and API testing to UAT, regression, smoke, and system testing.
                        </p>
                        <p>
                            On the automation side, I’ve built and maintained frameworks using Selenium (Java) and Playwright (TypeScript), following BDD/TDD practices and integrating tests into CI/CD pipelines with Azure DevOps. I regularly work with tools like JIRA, Confluence, Git, Postman, SoapUI, JMeter, k6, and Flood.io, and I’m comfortable with Java, TypeScript, and JavaScript.
                        </p>
                        <p className="font-bold text-gray-900">
                            Beyond tools and frameworks, what really matters to me is quality, usability, and accessibility.
                        </p>
                        <p>
                            I currently work as a Software Tester at INTO University Partnerships, and this accessibility scanner came from a simple idea:
                            <span className="block mt-2 text-primary-600 font-bold">👉 Accessibility shouldn’t be complicated or expensive to check.</span>
                        </p>
                        <p>
                            This tool is built from real-world testing experience — the kind you get after reviewing countless screens, edge cases, and “almost accessible” features. My goal is to make accessibility checks simple, practical, and usable for everyday teams, not just specialists.
                        </p>
                        <p className="text-xl italic text-primary-700 font-medium">
                            If this tool helps you catch issues early and build more inclusive products, then it’s doing exactly what I built it for. 😊
                        </p>
                    </div>
                </div>
            </div>

            {/* Technical Arsenal */}
            <div className="space-y-10">
                <div className="text-center space-y-4">
                    <h2 className="text-3xl font-black text-gray-900">Technical Arsenal</h2>
                    <p className="text-gray-500 font-medium">Core competencies and specialized expertise</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {skills.map((skill, i) => (
                        <div key={i} className="glass p-6 rounded-3xl border border-white/50 hover:shadow-xl transition-all group">
                            <div className="flex items-center justify-between mb-4">
                                <div className="p-3 bg-primary-50 text-primary-600 rounded-2xl group-hover:bg-primary-600 group-hover:text-white transition-colors">
                                    {skill.icon}
                                </div>
                                <span className="text-[10px] font-bold uppercase tracking-widest text-primary-500 bg-primary-50 px-2 py-1 rounded-full">
                                    {skill.level}
                                </span>
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-1">{skill.name}</h3>
                            <p className="text-sm text-gray-500">{skill.details}</p>
                        </div>
                    ))}
                </div>
            </div>


            {/* Call to Action */}
            <div className="text-center py-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Want to collaborate?</h2>
                <Link to="/contact" className="btn-primary py-4 px-10 rounded-2xl text-lg font-bold inline-flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Connect with Mohammad
                </Link>
            </div>
        </div>
    );
};
